import { Clapperboard, Coins, DollarSign, Film, Repeat, TrendingUp } from "lucide-react";
import { ceilDiv, count, divide, duration, futureDate, money } from "./format";
import type { Calculator } from "./types";

export const CALCULATORS: Calculator[] = [
  {
    slug: "views-to-money",
    related: [
      "/calculators/creator-money-calculator",
      "/calculators/cpm-rpm-calculator",
      "/use-cases/clippers",
    ],
    eyebrow: "Earnings goal",
    title: "How many views to make $10,000?",
    cardTitle: "Views to Money",
    description: "How many views and videos you need to hit your income goal.",
    seoTitle: "Views to Money Calculator: How Many Views to Make $10,000",
    seoDescription:
      "Free calculator. Enter your income goal and CPM to see how many views and videos you need to reach $10,000 or any amount on YouTube, TikTok, or Reels.",
    keyword: "how many views to make money",
    icon: TrendingUp,
    fields: [
      { key: "goal", label: "Income goal", type: "currency", default: 10000, min: 0, step: 500 },
      {
        key: "cpm",
        label: "CPM (earnings per 1,000 views)",
        type: "currency",
        default: 4,
        min: 0.01,
        step: 0.5,
        unit: "/1K",
      },
      {
        key: "avgViews",
        label: "Average views per video",
        type: "number",
        default: 25000,
        min: 1,
        step: 1000,
        unit: "views",
      },
    ],
    compute: ({ goal, cpm, avgViews }) => {
      const totalViews = ceilDiv(goal * 1000, cpm);
      const videos = totalViews == null ? null : ceilDiv(totalViews, avgViews);
      const revPerVideo = divide(avgViews * cpm, 1000);
      return {
        headline: {
          value: count(totalViews),
          label: `Views to make ${money(goal)}`,
          hint: "across all of your videos",
        },
        stats: [
          {
            label: "Videos needed",
            value: count(videos),
            hint: `at ${count(avgViews)} views each`,
          },
          { label: "Revenue per video", value: money(revPerVideo, 2) },
          { label: "Your CPM", value: money(cpm, 2), hint: "per 1,000 views" },
        ],
      };
    },
    bodyHeading: "How the views-to-money calculator works",
    body: [
      "This calculator works backward from your income goal. We divide your target dollar amount by your CPM, the amount you earn per 1,000 views, and multiply by 1,000 to find the total views required. We then divide that by your average views per video to estimate how many videos you need to post.",
      "CPM varies wildly by platform and niche. YouTube long-form can pay several dollars per 1,000 views, while YouTube Shorts, TikTok, and Instagram Reels typically pay far less. Plug in your real CPM from your analytics dashboard for the most accurate result.",
      "Most creators reach big view totals by posting many clips rather than landing one viral hit. Crayo turns a single long video, whether a stream, podcast, or interview, into dozens of short clips, so you can hit the view count your income goal demands without editing each one by hand.",
    ],
    faqs: [
      {
        question: "How many views do I need to make $10,000?",
        answer:
          "At a $4 CPM you need roughly 2.5 million views to make $10,000, since you earn $4 for every 1,000 views. Lower your CPM and the required views go up; raise it and they come down. Enter your own CPM above for an exact number.",
      },
      {
        question: "What is a good CPM for short-form video?",
        answer:
          "YouTube Shorts and TikTok often pay between $0.02 and $0.40 per 1,000 views, while YouTube long-form can pay $2 to $10 or more depending on niche and audience location. Finance, tech, and business niches tend to command the highest CPMs.",
      },
      {
        question: "Does posting more videos actually help me earn more?",
        answer:
          "Yes. Total earnings scale with total views, and more posts mean more chances to accumulate views and go viral. Repurposing one long video into many shorts is the fastest way to raise your post count without raising your editing time.",
      },
    ],
  },
  {
    slug: "posts-to-a-million-views",
    related: [
      "/calculators/posting-consistency-calculator",
      "/calculators/views-to-money",
      "/features/auto-clip",
    ],
    eyebrow: "View goal",
    title: "How many posts to get 1 million views?",
    cardTitle: "Posts to a Million Views",
    description: "See how many times you need to post to reach 1,000,000 views at your average.",
    seoTitle: "Posts to a Million Views Calculator: Videos to Hit 1M Views",
    seoDescription:
      "Free calculator showing how many posts you need to reach 1 million views based on your average, plus how long it will take at your posting rate.",
    keyword: "how many posts to get 1 million views",
    icon: Repeat,
    fields: [
      {
        key: "goal",
        label: "View goal",
        type: "number",
        default: 1000000,
        min: 1,
        step: 50000,
        unit: "views",
      },
      {
        key: "avgViews",
        label: "Average views per post",
        type: "number",
        default: 5000,
        min: 1,
        step: 500,
        unit: "views",
      },
      {
        key: "postsPerDay",
        label: "Posts per day",
        type: "number",
        default: 2,
        min: 1,
        max: 20,
        step: 1,
        unit: "/day",
      },
    ],
    compute: ({ goal, avgViews, postsPerDay }) => {
      const posts = ceilDiv(goal, avgViews);
      const days = posts == null ? null : ceilDiv(posts, postsPerDay);
      return {
        headline: {
          value: count(posts),
          label: `Posts to reach ${count(goal)} views`,
          hint: `at ${count(avgViews)} views per post`,
        },
        stats: [
          {
            label: "Time to reach goal",
            value: `${count(days)} days`,
            hint: `posting ${count(postsPerDay)}/day`,
          },
          { label: "Projected finish date", value: futureDate(days) },
        ],
      };
    },
    bodyHeading: "How the posts-to-a-million-views calculator works",
    body: [
      "We divide your total view goal by your average views per post to get the number of posts you need. We then divide that by how often you post to estimate a realistic timeline and projected finish date.",
      "The math rewards consistency. If your clips average 5,000 views, a million views is 200 posts, about three months at two posts a day. Doubling your daily output halves the timeline, which is why high-volume clippers treat posting cadence as their main growth lever.",
      "Posting twice a day every day is hard when each clip is edited by hand. Crayo removes that bottleneck by generating multiple ready-to-post shorts from every long video you upload, so hitting a million views becomes a question of cadence, not editing hours.",
    ],
    faqs: [
      {
        question: "How many videos do I need to get 1 million views?",
        answer:
          "It depends on your average views per video. At 5,000 views each you need 200 posts; at 10,000 views each you need 100. Enter your own average above to see your exact number.",
      },
      {
        question: "How long does it take to get a million views?",
        answer:
          "At two posts per day averaging 5,000 views, roughly 100 days. Posting more often, or raising your average views per post, shortens the timeline significantly.",
      },
      {
        question: "Is it better to post more or to make each video better?",
        answer:
          "Both compound, but volume is the more reliable lever for short-form. More posts mean more chances to hit the algorithm, and repurposing long video into many clips lets you increase volume without sacrificing quality.",
      },
    ],
  },
  {
    slug: "creator-money-calculator",
    related: [
      "/calculators/views-to-money",
      "/calculators/cpm-rpm-calculator",
      "/use-cases/faceless-youtube-channels",
    ],
    eyebrow: "Earnings estimate",
    title: "How much money do your views make?",
    cardTitle: "Creator Money",
    description: "Estimate how much your views are worth across YouTube, TikTok, and Reels.",
    seoTitle: "YouTube and TikTok Money Calculator: What Views Are Worth",
    seoDescription:
      "Free creator money calculator. Estimate your earnings from YouTube Shorts, YouTube long-form, TikTok, and Instagram Reels based on your view count and RPM.",
    keyword: "how much money do youtube views make",
    icon: DollarSign,
    fields: [
      {
        key: "platform",
        label: "Platform",
        type: "select",
        default: 0.05,
        options: [
          { label: "YouTube Shorts", value: 0.05 },
          { label: "YouTube long-form", value: 4 },
          { label: "TikTok (Creator Rewards)", value: 0.4 },
          { label: "Instagram Reels", value: 0.1 },
        ],
      },
      {
        key: "views",
        label: "Total views",
        type: "number",
        default: 1000000,
        min: 0,
        step: 50000,
        unit: "views",
      },
      {
        key: "rpm",
        label: "RPM (per 1,000 views)",
        type: "currency",
        default: 0.05,
        min: 0.01,
        step: 0.05,
        unit: "/1K",
        syncFrom: "platform",
        resettable: true,
      },
    ],
    compute: ({ views, rpm }) => {
      const earnings = divide(views * rpm, 1000);
      const viewsFor100 = ceilDiv(100 * 1000, rpm);
      const viewsFor1000 = ceilDiv(1000 * 1000, rpm);
      return {
        headline: {
          value: money(earnings, 2),
          label: `Estimated earnings from ${count(views)} views`,
        },
        stats: [
          { label: "Effective RPM", value: money(rpm, 2), hint: "per 1,000 views" },
          { label: "Views for $100", value: count(viewsFor100) },
          { label: "Views for $1,000", value: count(viewsFor1000) },
        ],
        note: "RPMs are platform averages and vary by niche, season, and audience location. Enter your real RPM above for an exact figure.",
      };
    },
    bodyHeading: "How the creator money calculator works",
    body: [
      "Enter your view count and platform, and we multiply your views (in thousands) by that platform's typical RPM, the revenue you keep per 1,000 views, to estimate your earnings. You can override the rate with your own number from your analytics for an exact figure.",
      "RPMs differ dramatically across platforms. YouTube long-form pays far more per view than Shorts, TikTok, or Reels, because long-form runs more and higher-value ads. That's why many creators post short clips to build reach, then funnel that audience toward longer, better-monetized content.",
      "Because payouts scale directly with views, the creators who earn the most are usually the ones who publish the most. Crayo helps you multiply your earning surface area by turning one long video into many shorts across every platform at once.",
    ],
    faqs: [
      {
        question: "How much money is 1 million views worth?",
        answer:
          "It depends entirely on the platform and RPM. At a $0.05 Shorts RPM, 1 million views is about $50; at a $4 long-form RPM it's around $4,000. Select your platform above to see an estimate.",
      },
      {
        question: "What is RPM and how is it different from CPM?",
        answer:
          "CPM is what advertisers pay per 1,000 impressions; RPM is what you actually keep per 1,000 views after the platform's cut. RPM is the more accurate number for estimating real creator earnings.",
      },
      {
        question: "Why does YouTube pay more than TikTok per view?",
        answer:
          "YouTube long-form shows more ads per view and shares ad revenue directly, while TikTok and Shorts pay from pooled creator funds that spread payouts across far more videos, resulting in much lower per-view rates.",
      },
    ],
  },
  {
    slug: "cpm-rpm-calculator",
    related: [
      "/calculators/views-to-money",
      "/calculators/creator-money-calculator",
      "/use-cases/faceless-youtube-channels",
    ],
    eyebrow: "Rate math",
    title: "CPM & RPM Calculator",
    cardTitle: "CPM & RPM",
    description: "Find your CPM, RPM, earnings, or the views you need.",
    seoTitle: "CPM and RPM Calculator: Calculate Your YouTube and TikTok Rate",
    seoDescription:
      "Free CPM and RPM calculator. Solve for your rate, total earnings, or the views you need. Enter any two values and we calculate the third instantly.",
    keyword: "cpm rpm calculator",
    icon: Coins,
    fields: [
      {
        key: "mode",
        label: "I want to calculate",
        type: "select",
        default: 0,
        options: [
          { label: "My RPM / CPM", value: 0 },
          { label: "My total earnings", value: 1 },
          { label: "Views I need", value: 2 },
        ],
      },
      {
        key: "earnings",
        label: "Total earnings",
        type: "currency",
        default: 500,
        min: 0,
        step: 50,
        showIf: ({ mode }) => mode !== 1,
      },
      {
        key: "views",
        label: "Total views",
        type: "number",
        default: 1000000,
        min: 0,
        step: 50000,
        unit: "views",
        showIf: ({ mode }) => mode !== 2,
      },
      {
        key: "rate",
        label: "Rate (per 1,000 views)",
        type: "currency",
        default: 4,
        min: 0.01,
        step: 0.5,
        unit: "/1K",
        showIf: ({ mode }) => mode !== 0,
      },
    ],
    compute: ({ mode, earnings, views, rate }) => {
      if (mode === 0) {
        const solved = divide(earnings * 1000, views);
        return {
          headline: { value: money(solved, 2), label: "Your RPM / CPM", hint: "per 1,000 views" },
          stats: [
            { label: "Earnings per 1M views", value: money(solved == null ? null : solved * 1000) },
            { label: "Based on", value: `${count(views)} views`, hint: money(earnings, 2) },
          ],
        };
      }
      if (mode === 1) {
        const solved = divide(views * rate, 1000);
        return {
          headline: { value: money(solved, 2), label: "Total earnings" },
          stats: [
            { label: "Earnings per 1M views", value: money(rate * 1000) },
            { label: "Based on", value: `${count(views)} views`, hint: `${money(rate, 2)} per 1K` },
          ],
        };
      }
      const solved = ceilDiv(earnings * 1000, rate);
      return {
        headline: { value: count(solved), label: `Views needed to earn ${money(earnings)}` },
        stats: [
          { label: "Earnings per 1M views", value: money(rate * 1000) },
          { label: "At a rate of", value: `${money(rate, 2)} / 1K`, hint: "CPM or RPM" },
        ],
      };
    },
    bodyHeading: "How the CPM & RPM calculator works",
    body: [
      "CPM and RPM both measure dollars earned per 1,000 views, so they share one identity: earnings equal views (in thousands) multiplied by your rate. This calculator solves that identity in whichever direction you need, so pick what to calculate and fill in the other two values.",
      "Use it to reverse-engineer your real rate from a past payout, to project earnings for a video before it lands, or to work out how many views a target payday requires. Knowing your true RPM turns every new clip into a number you can plan around.",
      "The clippers who scale fastest track their rate obsessively, then push volume to multiply it. Crayo is how they keep that volume up, with one long video in and a batch of ready-to-post shorts out.",
    ],
    faqs: [
      {
        question: "How do you calculate CPM?",
        answer:
          "Divide your total earnings by your total views, then multiply by 1,000. For example, $500 from 1,000,000 views is a $0.50 CPM. Select 'My RPM / CPM' above and the calculator does it for you.",
      },
      {
        question: "What's the difference between CPM and RPM?",
        answer:
          "CPM is the advertiser-side cost per 1,000 impressions; RPM is your revenue per 1,000 views after the platform's share. The arithmetic is identical, which is why this one tool handles both.",
      },
      {
        question: "How many views do I need to earn a specific amount?",
        answer:
          "Choose 'Views I need', enter your target earnings and your rate, and the calculator divides your goal by the rate to return the required view count.",
      },
    ],
  },
  {
    slug: "clips-from-one-video",
    related: ["/features/auto-clip", "/use-cases/podcast-clipping", "/calculators/views-to-money"],
    eyebrow: "Repurposing",
    title: "How many clips can you make from one video?",
    cardTitle: "Clips From One Video",
    description:
      "See how many short clips, and how many views, you can squeeze out of one long video.",
    seoTitle: "Clips From One Video Calculator: Shorts From a Long Video",
    seoDescription:
      "Free calculator showing how many short clips you can make from one long video, plus the projected views and short-form runtime from a single upload.",
    keyword: "how many clips from one video",
    icon: Film,
    fields: [
      {
        key: "length",
        label: "Long video length",
        type: "number",
        default: 60,
        min: 1,
        step: 5,
        unit: "min",
      },
      {
        key: "clipLength",
        label: "Average clip length",
        type: "number",
        default: 45,
        min: 5,
        step: 5,
        unit: "sec",
      },
      {
        key: "usable",
        label: "Usable footage",
        type: "number",
        default: 40,
        min: 1,
        max: 100,
        step: 5,
        unit: "%",
      },
      {
        key: "avgViews",
        label: "Average views per clip",
        type: "number",
        default: 5000,
        min: 1,
        step: 500,
        unit: "views",
      },
    ],
    compute: ({ length, clipLength, usable, avgViews }) => {
      const usableSeconds = length * 60 * (usable / 100);
      const clips = clipLength > 0 ? Math.floor(usableSeconds / clipLength) : null;
      const projViews = clips == null ? null : clips * avgViews;
      const runtime = clips == null ? null : clips * clipLength;
      return {
        headline: {
          value: count(clips),
          label: "Short clips from one video",
          hint: `from ${count(length)} minutes of footage`,
        },
        stats: [
          {
            label: "Projected total views",
            value: count(projViews),
            hint: `at ${count(avgViews)} per clip`,
          },
          { label: "Total short-form runtime", value: duration(runtime) },
          { label: "Usable footage", value: `${count(usable)}%`, hint: "of the source video" },
        ],
      };
    },
    bodyHeading: "How the clips-from-one-video calculator works",
    body: [
      "We convert your long video into seconds, apply the share of footage that's actually clip-worthy, and divide by your target clip length to estimate how many shorts you can produce. Multiply that by your average views per clip and you have the projected reach of a single upload.",
      "A 60-minute stream or podcast at 40% usable footage holds roughly 32 clips at 45 seconds each. That's a month of daily posts from one recording, the core reason repurposing beats creating from scratch.",
      "This is exactly what Crayo automates. Drop in one long video and get back a batch of ready-to-post shorts with captions, hooks, and formatting, and no need to scrub a timeline for the good moments by hand.",
    ],
    faqs: [
      {
        question: "How many shorts can you make from a long video?",
        answer:
          "It depends on the length and how much is usable. A 60-minute video with 40% clip-worthy footage yields about 32 clips at 45 seconds each. Adjust the inputs above for your own footage.",
      },
      {
        question: "What counts as 'usable footage'?",
        answer:
          "Usable footage is the portion of your video that makes a strong standalone clip, such as punchy moments, stories, and hot takes, excluding dead air, intros, and filler. Most long-form content lands between 30% and 50%.",
      },
      {
        question: "Is it worth turning one video into many clips?",
        answer:
          "Yes. Repurposing multiplies the reach of work you've already done, feeds an aggressive posting schedule, and lets you test which moments resonate, all without filming anything new.",
      },
    ],
  },
  {
    slug: "posting-consistency-calculator",
    related: [
      "/calculators/posts-to-a-million-views",
      "/features/auto-clip",
      "/use-cases/brainrot-channels",
    ],
    eyebrow: "Posting schedule",
    title: "How many videos should you post?",
    cardTitle: "Posting Consistency",
    description:
      "Build a realistic posting schedule and see the views and clips it takes to hit your goal.",
    seoTitle: "Posting Consistency Calculator: How Many Videos Should I Post",
    seoDescription:
      "Free calculator. Plan your posting schedule and see total posts, projected views, and how many clips you need from each long video to keep the pace.",
    keyword: "how many videos should i post",
    icon: Clapperboard,
    fields: [
      {
        key: "postsPerDay",
        label: "Posts per day",
        type: "number",
        default: 3,
        min: 1,
        max: 20,
        step: 1,
        unit: "/day",
      },
      {
        key: "days",
        label: "Goal length",
        type: "number",
        default: 90,
        min: 1,
        step: 5,
        unit: "days",
      },
      {
        key: "avgViews",
        label: "Average views per post",
        type: "number",
        default: 4000,
        min: 1,
        step: 500,
        unit: "views",
      },
      {
        key: "sourcesPerWeek",
        label: "Long videos sourced per week",
        type: "number",
        default: 2,
        min: 1,
        step: 1,
        unit: "/week",
      },
    ],
    compute: ({ postsPerDay, days, avgViews, sourcesPerWeek }) => {
      const totalPosts = postsPerDay * days;
      const projViews = totalPosts * avgViews;
      const sources = Math.ceil(sourcesPerWeek * (days / 7));
      const clipsPer = ceilDiv(totalPosts, sources);
      return {
        headline: {
          value: count(totalPosts),
          label: `Posts over ${count(days)} days`,
          hint: `at ${count(postsPerDay)} per day`,
        },
        stats: [
          {
            label: "Projected total views",
            value: count(projViews),
            hint: `at ${count(avgViews)} per post`,
          },
          { label: "Clips per long video", value: count(clipsPer), hint: "to sustain the pace" },
          { label: "Long videos needed", value: count(sources), hint: "over the period" },
        ],
      };
    },
    bodyHeading: "How the posting consistency calculator works",
    body: [
      "We multiply your daily posting rate by your goal length to project total posts and total views, then work out how many clips you'd need from each long video to keep that pace. The result shows the real volume consistency demands.",
      "Three posts a day for 90 days is 270 clips. Sourcing two long videos a week, that's about ten clips per video, a target that's impossible to hit by editing each one manually, but routine when you repurpose in batches.",
      "Crayo closes the gap between the schedule you want and the schedule you can sustain. Each long video becomes enough clips to feed an aggressive cadence automatically, so consistency stops being a function of free time.",
    ],
    faqs: [
      {
        question: "How many times a day should I post short-form video?",
        answer:
          "Most growing creators post one to three times per day. More posts mean more chances with the algorithm, but only if you can sustain the pace, so use the calculator to find a cadence you can actually keep.",
      },
      {
        question: "How many clips do I need from each long video?",
        answer:
          "Divide your total planned posts by the number of long videos you can source in that period. The calculator does this for you and shows the clips-per-video target above.",
      },
      {
        question: "Why does posting consistency matter so much?",
        answer:
          "Short-form algorithms reward fresh, frequent uploads. A steady cadence compounds reach over time, while sporadic posting resets your momentum. Repurposing makes a steady cadence realistic.",
      },
    ],
  },
];

export const getCalculator = (slug: string) => CALCULATORS.find((c) => c.slug === slug);

export const getOtherCalculators = (slug: string) => CALCULATORS.filter((c) => c.slug !== slug);
